import React, { useState, useEffect, useContext, createContext } from 'react';
import { ShoppingCart, CheckCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import api from '../services/api';

// ✅ Funciones de seguridad y parseo
const parsePresentaciones = (pres) => {
  if (Array.isArray(pres)) return pres;
  if (typeof pres === 'string') {
    try { return JSON.parse(pres); } catch { return []; }
  }
  return [];
};

const parseImagenesExtra = (img) => {
  if (Array.isArray(img)) return img;
  if (typeof img === 'string') return img.split(',').filter(Boolean);
  return [];
};

// ✅ Lógica para obtener la URL correcta de la imagen (solo visual)
const getImageUrl = (imageName) => {
  if (!imageName) return '/images/default-product.jpg';
  if (imageName.startsWith('http')) return imageName;
  return `https://api.romanvalderrabanoelcatador.com/api/uploads/productos/${imageName}`;
};

// ESTILOS PUROS ENCAPSULADOS PARA ESTE COMPONENTE
const internalStyles = `
  .prod-page-wrapper {
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    background-color: #fafaf9;
    color: #1c1917;
    min-height: 100vh;
    padding: 64px 24px;
    box-sizing: border-box;
    position: relative;
  }
  
  .prod-container {
    max-width: 1280px;
    margin: 0 auto;
  }

  .prod-header {
    text-align: center;
    margin-bottom: 64px;
  }

  .prod-header h1 {
    font-size: clamp(2.5rem, 4vw, 3.5rem);
    font-weight: 900;
    color: #1c1917;
    margin: 0 0 16px 0;
  }

  .prod-header p {
    font-size: 1.125rem;
    color: #78716c;
    margin: 0;
  }

  /* Filtros */
  .prod-filters {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    margin-bottom: 48px;
  }

  .prod-filter-btn {
    padding: 12px 24px;
    border-radius: 50px;
    font-weight: bold;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid #e7e5e4;
    background: white;
    color: #57534e;
  }

  .prod-filter-btn:hover {
    background: #f5f5f4;
  }

  .prod-filter-btn.active {
    background: #1c1917;
    color: white;
    border-color: #1c1917;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }

  .prod-count {
    color: #78716c;
    font-weight: 500;
    margin-bottom: 32px;
    text-align: left;
  }

  /* Grid de Productos */
  .prod-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 32px;
  }
  @media(min-width: 768px) { .prod-grid { grid-template-columns: repeat(2, 1fr); } }
  @media(min-width: 1024px) { .prod-grid { grid-template-columns: repeat(3, 1fr); } }
  @media(min-width: 1280px) { .prod-grid { grid-template-columns: repeat(4, 1fr); } }

  /* Tarjeta de Producto */
  .prod-card {
    background: white;
    border-radius: 24px;
    padding: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    border: 1px solid #f5f5f4;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
  }

  .prod-card:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }

  .prod-img-container {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    margin-bottom: 20px;
    background: #f5f5f4;
  }

  .prod-img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    transition: transform 0.5s ease;
    display: block;
  }

  .prod-card:hover .prod-img {
    transform: scale(1.05);
  }

  .prod-title {
    font-size: 1.25rem;
    font-weight: bold;
    color: #292524;
    margin: 0 0 8px 0;
    line-height: 1.2;
  }

  .prod-category {
    font-size: 0.75rem;
    font-weight: bold;
    color: #059669;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 24px 0;
  }

  .prod-footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 1px solid #f5f5f4;
  }

  .prod-price {
    font-size: 1.5rem;
    font-weight: 900;
    color: #292524;
  }

  .prod-add-btn {
    background: #1c1917;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 12px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .prod-add-btn:hover, .prod-add-btn:active {
    background: #059669;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(5, 150, 105, 0.3);
  }

  /* Estado Vacío / Loader */
  .prod-empty {
    background: white;
    border-radius: 24px;
    padding: 64px;
    text-align: center;
    border: 1px solid #f5f5f4;
  }

  .prod-empty-icon {
    font-size: 4rem;
    display: block;
    margin-bottom: 24px;
  }

  .prod-empty h3 {
    font-size: 1.5rem;
    margin: 0 0 8px 0;
    color: #292524;
  }

  .prod-empty p {
    color: #78716c;
    margin: 0 0 32px 0;
  }

  .prod-loader {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fafaf9;
  }

  .prod-spinner {
    width: 64px;
    height: 64px;
    border: 4px solid #a7f3d0;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Toast Notification */
  .prod-toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: #059669;
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 50;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: slideUp 0.3s ease-out;
  }
  @keyframes slideUp { 
    from { transform: translateY(100%); opacity: 0; } 
    to { transform: translateY(0); opacity: 1; } 
  }
`;

// Componente Tarjeta de Producto
const ProductCard = ({ product, onAddToCart }) => {
  const precio = typeof product.precio === 'number' ? product.precio : parseFloat(product.precio) || 0;
  
  // Obtenemos la ruta limpia extrayendo imagen_portada o la primera extra
  const rawImage = product.imagen_portada || product.imagenes_extra?.[0];
  const imagen = getImageUrl(rawImage);
  
  return (
    <div className="prod-card">
      <div className="prod-img-container">
        <img 
          src={imagen} 
          alt={product.nombre} 
          className="prod-img" 
          onError={(e) => { e.target.src = '/images/default-product.jpg'; }}
        />
      </div>
      <h3 className="prod-title">{product.nombre}</h3>
      <p className="prod-category">{product.categoria}</p>
      
      <div className="prod-footer">
        <span className="prod-price">${precio.toFixed(2)}</span>
        <button 
          onClick={() => onAddToCart(product)}
          className="prod-add-btn"
          title="Agregar al carrito"
        >
          <ShoppingCart size={20} />
          <span>Agregar</span>
        </button>
      </div>
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);
  
  const location = useLocation();
  const { addToCart } = useContext(CartContext) || { addToCart: () => {} };

  const categories = [
    'Todos',
    'Café',
    'Confitería de Café',
    'Licores',
    'Otros Productos'
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoria = params.get('categoria');
    if (categoria) {
      const categoriaMap = {
        'cafe': 'Café',
        'confiteria': 'Confitería de Café',
        'licores': 'Licores',
        'otros': 'Otros Productos'
      };
      setSelectedCategory(categoriaMap[categoria] || 'Todos');
    }
  }, [location]);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    let filtered = [...products];
    if (selectedCategory !== 'Todos') {
      filtered = filtered.filter(p => p.categoria === selectedCategory);
    }
    setFilteredProducts(filtered);
  }, [selectedCategory, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/productos');
      const data = response.data;
      
      if (data && data.length > 0) {
        const productosProcesados = data.map(producto => ({
          ...producto,
          presentaciones: parsePresentaciones(producto.presentaciones),
          imagenes_extra: parseImagenesExtra(producto.imagenes_extra)
        }));
        
        setProducts(productosProcesados);
        setFilteredProducts(productosProcesados);
      } else {
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ CORRECCIÓN DE LA IMAGEN: Ahora pasamos la URL COMPLETA al Carrito
  const handleAddToCart = (product) => {
    const precioBase = typeof product.precio === 'number' ? product.precio : parseFloat(product.precio) || 0;
    const rawImage = product.imagen_portada || product.imagenes_extra?.[0];
    
    if (addToCart) {
      addToCart({
        id: product.id,
        name: product.nombre,
        price: precioBase,
        image: getImageUrl(rawImage) // <-- Solución para que el carrito muestre la imagen
      }, 1);
    }
    
    // Mostrar el toast de inmediato
    setToastMessage(`¡${product.nombre} agregado al carrito!`);
    
    // Ocultar el toast después de 3 segundos
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  if (loading) {
    return (
      <div className="prod-wrapper">
        <style>{internalStyles}</style>
        <div className="prod-loader">
          <div className="prod-spinner"></div>
          <p style={{marginTop: '16px', color: '#57534e', fontWeight: '500'}}>Cargando catálogo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prod-page-wrapper">
      <style>{internalStyles}</style>

      {/* Notificación Toast */}
      {toastMessage && (
        <div className="prod-toast">
          <CheckCircle size={20} />
          <span>{toastMessage}</span>
        </div>
      )}

      <div className="prod-container">
        
        <div className="prod-header">
          <h1>Nuestros Productos</h1>
          <p>Café · Confitería de Café · Licores · Otros Productos</p>
        </div>

        <div className="prod-filters">
          {categories.map(category => (
            <button
              key={category}
              className={`prod-filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="prod-count">
          Mostrando {filteredProducts.length} de {products.length} productos
        </div>

        {filteredProducts.length === 0 ? (
          <div className="prod-empty">
            <span className="prod-empty-icon">😕</span>
            <h3>No se encontraron productos</h3>
            <p>Intenta seleccionando otra categoría</p>
            <button 
              className="prod-add-btn" 
              style={{ margin: '0 auto' }}
              onClick={() => setSelectedCategory('Todos')}
            >
              Ver todos los productos
            </button>
          </div>
        ) : (
          <div className="prod-grid">
            {filteredProducts.map(product => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onAddToCart={handleAddToCart} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;